import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import env from "../../../../config/env";
import "./index.css";
import CommonModal from "../../../Settings/components/ResetContext/components/DumpModal";
import IntegrationModal from "./IntegrationModal";
import PromptIntroStep from "./components/PromptIntroStep";
import PromptDetailsForm from "./components/PromptDetailsForm";
import DeleteTaskModal from "./components/DeleteTaskModal";
import {
  createInitialOutputOption,
  createProviderSelectOption,
  DEFAULT_OUTPUT_OPTION,
  formatToTimeInput,
  isChannelProvider,
  mergeChannels,
  normalizeProviderId,
  parseChannelsPayload,
  parseProvidersPayload,
  convertLocalToUTC,
} from "./utils";
import { useUserInfo } from "../../../../context/user";
import toast from "react-hot-toast";
import { saveUnfinishedTaskDraft, plainText } from "../../../../lib/taskDrafts";
import {
  updateTaskDraft,
  removeTaskDraftById,
  removeUnfinishedDraftMatching,
} from "../../../../queries/taskDrafts";
import moment from "moment-timezone";
import { request, ReqMethods } from "../../../../lib/apiClient";
import {
  useProviderChannels,
  usePromptProviders,
  useGenerateTaskPrompt,
  useSaveTaskPrompt,
  useDeleteTaskPrompt,
} from "../../../../queries/tasks";

const { POST } = ReqMethods;

const PromptModal = ({
  isOpen,
  isModalOpen,
  onClose,
  justCloseIt,
  getUpdatePromptsData,
  editadata,
  setIsEditPrompt,
  utcOffset,
  autoloadfirsttext,
  resumeTaskId,
  resumeTaskTitle,
  resumeTaskContent,
  assistantData,
  asistantSelected,
  timeWithZone,
  time_zone,
  renderAs = "modal",
  pageHeader = null,
  pageFooter = null,
  composerAttachSlot = null,
}) => {
  const BASE_URL = `${env.API_URL}/v1`;
  const isPageMode = renderAs === "page";
  const modalIsOpen = isPageMode
    ? true
    : typeof isOpen === "boolean"
      ? isOpen
      : Boolean(isModalOpen);
  const isEdit = Boolean(editadata);
  const isScheduledTask =
    editadata?.repeat && editadata.repeat.toLowerCase() !== "none";
  const selectedAssistant = asistantSelected || assistantData?.[0] || null;

  const [userInfo, , , , userActiveLocation] = useUserInfo();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutateAsync: fetchProviderChannels } = useProviderChannels();
  const { mutateAsync: fetchPromptProviders } = usePromptProviders();
  const { mutateAsync: generateTaskPrompt } = useGenerateTaskPrompt();
  const { mutateAsync: saveTaskPrompt } = useSaveTaskPrompt();
  const { mutateAsync: deleteTaskPrompt } = useDeleteTaskPrompt();

  const [processing, setProcessing] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [askToDeleteModal, setAskToDeleteModal] = useState(false);

  const [promptName, setPromptName] = useState("");
  const [promptContent, setPromptContent] = useState("");
  const [promptSetAs, setPromptSetAs] = useState("personal");
  const [outputOption, setOutputOption] = useState(DEFAULT_OUTPUT_OPTION);
  const [slackChannel, setSlackChannel] = useState("");
  const [openIntegrationModal, setOpenIntegrationModal] = useState(false);
  const [requiredIntegrations, setRequiredIntegrations] = useState([]);
  const [integrationId, setIntegrationId] = useState(null);
  const [utcTime, setUtcTime] = useState(null);
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(moment().format("YYYY-MM-DD"));
  const [scheduleRunCount, setScheduleRunCount] = useState(1);
  const [scheduleFrequency, setScheduleFrequency] = useState("none");
  const [selectedWebhookId, setSelectedWebhookId] = useState(null);
  const [scheduleTime, setScheduleTime] = useState("");
  const [isScheduleActive, setIsScheduleActive] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(1);

  const [channelList, setChannelList] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingMoreChannels, setLoadingMoreChannels] = useState(false);
  const [hasMoreChannels, setHasMoreChannels] = useState(false);
  const [nextChannelPageToken, setNextChannelPageToken] = useState(null);
  const [channelSearchQuery, setChannelSearchQuery] = useState("");
  const [activeChannelProvider, setActiveChannelProvider] = useState(null);
  const [activeChannelIntegration, setActiveChannelIntegration] =
    useState(null);
  const [rqIntegration, setRqIntegration] = useState(null);
  const channelSearchTimer = useRef(null);

  const [promptNameError, setPromptNameError] = useState(null);
  const [promptContentError, setPromptContentError] = useState(null);
  const [promptStep, setPromptStep] = useState(0);
  const [promptQuery, setPromptQuery] = useState("");
  const [promptQueryError, setPromptQueryError] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Mirrors for the leave-detection effect below, so its listeners (added
  // once on mount) always see the latest values instead of a stale closure.
  const hasSavedRef = useRef(false);
  const promptStepRef = useRef(promptStep);
  const promptNameRef = useRef(promptName);
  const promptContentRef = useRef(promptContent);
  // Set once, at init, when this session resumed a specific "Unfinished
  // Tasks" draft — re-abandoning it then updates that same row in place
  // (instead of leaving it untouched and creating a second one), and saving
  // successfully removes exactly that row by _id (not a content guess).
  const resumingDraftIdRef = useRef(null);
  // pagehide and beforeunload both fire on a real tab close — without this,
  // a single close attempts the unfinished-draft save twice (two POSTs, two
  // rows in Drafts for the same abandoned attempt).
  const unfinishedSaveAttemptedRef = useRef(false);

  const closeModal = () => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }

    if (typeof justCloseIt === "function") {
      justCloseIt();
    }
  };

  const resetChannelState = useCallback(() => {
    setChannelList([]);
    setSlackChannel("");
    setHasMoreChannels(false);
    setNextChannelPageToken(null);
    setChannelSearchQuery("");
    setActiveChannelProvider(null);
    setActiveChannelIntegration(null);
  }, []);

  useEffect(() => {
    if (!modalIsOpen) return;

    setProcessing(false);
    setDeleteProcessing(false);
    setAskToDeleteModal(false);
    resetChannelState();

    // A resumed "Unfinished Task" draft already has real title/body typed
    // in (unlike autoloadfirsttext, which only seeds the one-line "describe
    // it" box) — restore them directly rather than routing back through the
    // AI-generation step.
    const isResuming = !isEdit && Boolean(resumeTaskContent || resumeTaskTitle);
    resumingDraftIdRef.current = isResuming ? resumeTaskId || null : null;
    setPromptName(isResuming ? resumeTaskTitle || "" : editadata?.title || "");
    setPromptContent(
      isResuming
        ? resumeTaskContent || ""
        : editadata?.prompt_value ||
        '<p>Draft a client follow-up email. <strong><span style="background-color: rgb(254, 240, 138);">[Paste decisions here]</span></strong> Create a professional follow-up email from call notes, including actions and next steps. <strong><span style="background-color: rgb(254, 240, 138);">Follow-up email from call.</span></strong></p>',
    );
    setPromptSetAs(editadata?.set_as || "personal");
    setOutputOption(createInitialOutputOption(editadata?.output));
    setSlackChannel(editadata?.channel || "");
    setOpenIntegrationModal(false);
    setRequiredIntegrations([]);
    setIntegrationId(editadata?.integration_id || null);
    setUtcTime(editadata?.utcTime || null);
    setSelectedWeekDays(
      editadata?.selectedWeekDays?.length > 0 ? editadata?.selectedWeekDays : editadata?.weekdays || [],
    );
    setScheduleDate(editadata?.scheduleDate || "");
    setScheduleRunCount(editadata?.runCount || 1);
    setScheduleFrequency(editadata?.repeat || "none");
    setSelectedWebhookId(editadata?.webhook_source || null);
    setScheduleTime(
      editadata?.scheduleTime ? timeWithZone : "",
    );
    setIsScheduleActive(Boolean(editadata?.active));
    setEstimatedTime(
      editadata?.estimated_time !== undefined && editadata?.estimated_time !== null
        ? String(editadata.estimated_time)
        : 1,
    );

    setPromptNameError(null);
    setPromptContentError(null);
    setPromptStep(isEdit || isResuming ? 2 : 0);
    setPromptQuery(isEdit ? "" : autoloadfirsttext || "");
    setPromptQueryError(false);
    setRqIntegration(null);

    const seededTimestamp =
      editadata?.updatedAt ||
      editadata?.updated_at ||
      editadata?.createdAt ||
      editadata?.created_at ||
      null;
    setLastSavedAt(seededTimestamp ? new Date(seededTimestamp).getTime() : null);
  }, [
    autoloadfirsttext,
    resumeTaskId,
    resumeTaskTitle,
    resumeTaskContent,
    editadata,
    isEdit,
    modalIsOpen,
    resetChannelState,
  ]);

  useEffect(() => {
    promptStepRef.current = promptStep;
    promptNameRef.current = promptName;
    promptContentRef.current = promptContent;
  });

  // CU-123yxuaptmf: auto-save an in-progress, never-saved new Task as an
  // "Unfinished Tasks" draft if the page is left (in-app navigation, tab
  // close/refresh) before it's saved. Gated to promptStep 2 — the real
  // title/body form — so the placeholder seeded before AI generation runs
  // (see the init effect above) is never mistaken for real user content.
  useEffect(() => {
    if (isEdit) return undefined;

    const trySaveUnfinished = () => {
      if (hasSavedRef.current) return;
      if (promptStepRef.current !== 2) return;
      const title = (promptNameRef.current || "").trim();
      // Keep the real HTML — plainText is only for the emptiness check.
      // Saving the plain-text version here would mean a resumed draft puts
      // that flattened text back into the rich-text editor, permanently
      // losing bold, highlights and paragraph structure.
      const html = promptContentRef.current || "";
      if (!title && !plainText(html).trim()) return;
      if (unfinishedSaveAttemptedRef.current) return;
      unfinishedSaveAttemptedRef.current = true;
      const locationId = userActiveLocation?.id;
      // Re-leaving a resumed draft updates that same row — it must not also
      // spawn a second "Unfinished Tasks" entry for the same ongoing attempt.
      if (resumingDraftIdRef.current) {
        updateTaskDraft({ id: resumingDraftIdRef.current, locationId, title, prompt: html }, queryClient);
        return;
      }
      saveUnfinishedTaskDraft(
        {
          locationId,
          userId: userInfo?._id,
          title,
          prompt: html,
        },
        queryClient,
      );
    };

    const handlePageHide = () => trySaveUnfinished();
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
      trySaveUnfinished();
    };
  }, [isEdit, userActiveLocation?.id, userInfo?._id, queryClient]);

  useEffect(() => {
    return () => {
      if (channelSearchTimer.current) {
        clearTimeout(channelSearchTimer.current);
      }
    };
  }, []);

  const handleScheduleFrequencyChange = (frequency) => {
    setScheduleFrequency(frequency);
    setIsScheduleActive(frequency === "none" ? false : true);
  };

  const handleSchedulePause = () => {
    setIsScheduleActive((currentValue) => !currentValue);
  };

  const handleScheduleTimeChange = (time) => {
    setScheduleTime(time);
    setUtcTime(convertLocalToUTC(time, utcOffset));
  };

  const loadChannels = useCallback(
    async (providerId, integration, fetchOptions = {}) => {
      const {
        search = "",
        pageToken = "",
        append = false,
        preferredChannelId = null,
      } = fetchOptions;

      if (!isChannelProvider(providerId)) return;

      try {
        if (append) {
          setLoadingMoreChannels(true);
        } else {
          setLoadingChannels(true);
        }

        setActiveChannelProvider(providerId || null);
        setActiveChannelIntegration(integration || null);

        if (!append) {
          setChannelSearchQuery(search);
        }

        const response = await fetchProviderChannels({
          profileId: userActiveLocation?.id,
          userId: userInfo?.userId,
          providerId,
          integration,
          search,
          pageToken,
        });

        const providerPayload = response?.data || {};
        const { channels, pagination } = parseChannelsPayload(providerPayload);
        const normalizedChannels = Array.isArray(channels) ? channels : [];

        setChannelList((previousChannels) =>
          append
            ? mergeChannels(previousChannels, normalizedChannels)
            : normalizedChannels,
        );

        const nextPageToken = pagination?.next_page_token || null;
        setNextChannelPageToken(nextPageToken);
        setHasMoreChannels(Boolean(pagination?.has_next && nextPageToken));

        if (!append) {
          setSlackChannel((currentChannel) => {
            if (
              currentChannel &&
              normalizedChannels.some(
                (channel) => channel.channel_id === currentChannel,
              )
            ) {
              return currentChannel;
            }

            if (
              preferredChannelId &&
              normalizedChannels.some(
                (channel) => channel.channel_id === preferredChannelId,
              )
            ) {
              return preferredChannelId;
            }

            return normalizedChannels[0]?.channel_id || "";
          });
        }
      } catch (error) {
        console.error("Error fetching provider channels:", error);
        toast.error("Unable to load channels right now.");
      } finally {
        setLoadingChannels(false);
        setLoadingMoreChannels(false);
      }
    },
    [fetchProviderChannels, userInfo?.userId, userActiveLocation?.id],
  );

  const fetchProviders = useCallback(
    async (targetProvider = null) => {
      if (!userActiveLocation?.id || !userInfo?.userId) return;

      try {
        const response = await fetchPromptProviders({
          profileId: userActiveLocation?.id,
          userId: userInfo?.userId,
          agencyId: userInfo?.agency_id,
        });

        const providers = parseProvidersPayload(
          response?.data?.data || {},
        );
        setRequiredIntegrations(providers);

        const nextProviderId = normalizeProviderId(
          targetProvider || editadata?.output,
        );

        if (!isChannelProvider(nextProviderId)) return;

        const provider = providers.find(
          (item) => normalizeProviderId(item) === nextProviderId,
        );

        if (!provider) return;

        const nextOption = createProviderSelectOption(provider);
        const nextIntegrationId = provider.integration_id || null;

        setOutputOption(nextOption);
        setIntegrationId(nextIntegrationId);

        if (!provider.connected) {
          setRqIntegration(nextProviderId);
          setOpenIntegrationModal(true);
          return;
        }

        setOpenIntegrationModal(false);
        await loadChannels(nextProviderId, nextIntegrationId, {
          search: "",
          pageToken: "",
          append: false,
          preferredChannelId: editadata?.channel || null,
        });
      } catch (error) {
        console.error("Error fetching providers:", error);
        toast.error("Unable to load output providers right now.");
      }
    },
    [
      fetchPromptProviders,
      userInfo?.userId,
      userInfo?.agency_id,
      userActiveLocation?.id,
      editadata?.channel,
      editadata?.output,
      loadChannels,
    ],
  );

  useEffect(() => {
    if (!modalIsOpen) return;
    fetchProviders();
  }, [fetchProviders, modalIsOpen]);

  const handleOutputOptionChange = async (option) => {
    const nextOption = option || DEFAULT_OUTPUT_OPTION;
    const providerId = normalizeProviderId(nextOption?.value);

    setOutputOption(nextOption);

    if (channelSearchTimer.current) {
      clearTimeout(channelSearchTimer.current);
    }

    resetChannelState();

    if (!isChannelProvider(providerId)) {
      setIntegrationId(null);
      setOpenIntegrationModal(false);
      setRqIntegration(null);
      return;
    }

    const provider = requiredIntegrations.find(
      (item) => normalizeProviderId(item) === providerId,
    );
    const nextIntegrationId = provider?.integration_id || null;

    setIntegrationId(nextIntegrationId);
    setActiveChannelProvider(providerId);
    setActiveChannelIntegration(nextIntegrationId);

    if (provider && !provider.connected) {
      setRqIntegration(providerId);
      setOpenIntegrationModal(true);
      return;
    }

    setOpenIntegrationModal(false);
    await loadChannels(providerId, nextIntegrationId, {
      search: "",
      pageToken: "",
      append: false,
    });
  };

  const handleChannelSearchInputChange = (inputValue, actionMeta) => {
    if (actionMeta?.action !== "input-change") return inputValue;

    const searchValue = inputValue || "";
    setChannelSearchQuery(searchValue);

    if (channelSearchTimer.current) {
      clearTimeout(channelSearchTimer.current);
    }

    if (!activeChannelProvider) return inputValue;

    channelSearchTimer.current = setTimeout(() => {
      loadChannels(activeChannelProvider, activeChannelIntegration, {
        search: searchValue,
        pageToken: "",
        append: false,
      });
    }, 350);

    return inputValue;
  };

  const handleChannelMenuScrollToBottom = () => {
    if (!activeChannelProvider) return;
    if (!hasMoreChannels || !nextChannelPageToken) return;
    if (loadingChannels || loadingMoreChannels) return;

    loadChannels(activeChannelProvider, activeChannelIntegration, {
      search: channelSearchQuery,
      pageToken: nextChannelPageToken,
      append: true,
    });
  };

  const handlePromptSubmit = async () => {
    if (processing) return;

    setPromptNameError(null);
    setPromptContentError(null);

    let hasError = false;

    if (!promptName.trim()) {
      setPromptNameError("Prompt Name is required");
      hasError = true;
    }

    if (!promptContent.trim()) {
      setPromptContentError("Prompt Content is required");
      hasError = true;
    }

    if (hasError) return;

    setProcessing(true);

    try {
      const authorEntry = {
        id: userInfo?._id,
        type: "author",
        username: userInfo?.userName,
      };

      const existingUserIds = Array.isArray(editadata?.userId)
        ? editadata.userId
        : [];
      const sharedUserEntries = existingUserIds.filter(
        (entry) => entry?.type === "shared",
      );
      const userIdPayload = isEdit
        ? [authorEntry, ...sharedUserEntries]
        : [authorEntry];

      // Label for the id we hold, in the same `#name` shape the picker shows.
      // Falls back to whatever the task was saved with, so re-saving a task
      // without touching the picker doesn't blank the name.
      const selectedChannelMeta = channelList.find(
        (channel) => channel.channel_id === slackChannel,
      );
      const selectedChannelName = selectedChannelMeta?.channel_name
        ? `#${selectedChannelMeta.channel_name}`
        : editadata?.channel_name || "";

      const payload = {
        title: promptName.trim(),
        category: editadata?.category || "",
        sub_category: editadata?.sub_category || "",
        prompt_value: promptContent,
        type: "user",
        userId: userIdPayload,
        locationId: userActiveLocation?.id,
        estimated_time: estimatedTime,
        set_as: promptSetAs,
        output: outputOption?.value || DEFAULT_OUTPUT_OPTION.value,
        channel: isChannelProvider(outputOption?.value) ? slackChannel : "",
        channel_name: isChannelProvider(outputOption?.value)
          ? selectedChannelName
          : "",
        scheduleTime: `${scheduleDate || moment.tz(time_zone).format("YYYY-MM-DD")} ${scheduleTime || ""}`,
        repeat: scheduleFrequency,
        active: scheduleFrequency === "none" ? false : isScheduleActive,
        utcTime: scheduleFrequency === "none" ? null : utcTime,
        time_zone: time_zone,
        webhook_source:
          scheduleFrequency === "webhook" ? selectedWebhookId : null,
      };

      if (integrationId) {
        payload.integration_id = integrationId;
      }

      if (
        scheduleFrequency === "weekly" ||
        scheduleFrequency === "weekdays" ||
        scheduleFrequency === "weekends"
      ) {
        payload.weekDays = selectedWeekDays;
        payload.runCount = scheduleRunCount;
      }

      if (scheduleFrequency === "monthly" || scheduleFrequency === "yearly") {
        payload.scheduleDate = scheduleDate;
        payload.runCount = scheduleRunCount;
      }

      console.log("[PromptModal] save payload:", payload);

      let savedTaskId = editadata?._id;
      if (isEdit) {
        payload._id = editadata?._id;
        await saveTaskPrompt({ isEdit: true, payload });
      } else {
        payload.assistant_name = selectedAssistant?.assistant_name;
        payload.assistant_id = selectedAssistant?.assistant_id;
        const createRes = await saveTaskPrompt({ isEdit: false, payload });
        savedTaskId = createRes?.data?._id || savedTaskId;
        // This Task is now real and saved — the leave-detection effect must
        // not also file it as an "Unfinished Tasks" draft.
        hasSavedRef.current = true;
        // A stale "Unfinished Tasks" draft must not keep sitting in Drafts
        // now that it's a real, saved Task. If this session resumed a
        // specific draft, remove exactly that row by _id; otherwise fall
        // back to a content match (e.g. redone from scratch after the
        // original draft's tab was closed, with no "Open" click in between).
        if (resumingDraftIdRef.current) {
          removeTaskDraftById(
            {
              id: resumingDraftIdRef.current,
              locationId: userActiveLocation?.id,
            },
            queryClient,
          );
        } else {
          removeUnfinishedDraftMatching(
            {
              locationId: userActiveLocation?.id,
              title: promptName,
              // Must match what was actually saved as the draft's prompt —
              // the raw HTML, not its plain-text rendering.
              prompt: promptContent,
            },
            queryClient,
          );
        }
      }

      // Link the selected webhook to the task via the convoy endpoint
      // (POST /convoy/sources/:source_id/tasks/:task_id). Fire-and-forget:
      // called directly through apiClient's request() rather than a
      // useMutation, and its own try/catch keeps a failure here from failing
      // the parent save above.
      if (scheduleFrequency === "webhook" && selectedWebhookId && savedTaskId) {
        try {
          await request(
            POST,
            `/convoy/sources/${selectedWebhookId}/tasks/${savedTaskId}`,
            {
              payload: {
                user_id: userInfo?._id,
                endpoint_url: `${BASE_URL}/convoy/receive/${selectedWebhookId}/${savedTaskId}`,
              },
            },
          );
        } catch (linkError) {
          console.error("Error linking webhook to task:", linkError);
        }
      }

      setLastSavedAt(Date.now());
      getUpdatePromptsData?.();

      if (typeof setIsEditPrompt === "function") {
        setIsEditPrompt(false);
      }

      if (isPageMode && isEdit) {
        toast.success("Task saved");
      } else {
        closeModal();
      }
    } catch (error) {
      console.error("Error saving task prompt:", error);
      toast.error("Something went wrong while saving the task.");
    } finally {
      setProcessing(false);
    }
  };

  const handleGeneratePrompt = async () => {
    if (!promptQuery.trim()) {
      setPromptQueryError(true);
      return;
    }

    setPromptQueryError(false);
    setPromptStep(1);

    try {
      const { title, content } = await generateTaskPrompt({
        agencyId: userActiveLocation?.agency_id,
        locationId: userActiveLocation?.id,
        username: userInfo?.userName,
        promptQuery: promptQuery.trim(),
        userId: userInfo?.userId,
        fallbackAssistantId: selectedAssistant?.assistant_id,
      });

      setPromptStep(2);
      setPromptName(title);
      setPromptContent(content);
    } catch (error) {
      console.error("Error generating task prompt:", error);
      toast.error("Unable to generate the task right now.");
      setPromptStep(5);
    }
  };

  const handleDeleteTask = async () => {
    if (!editadata?._id || deleteProcessing) return;

    setDeleteProcessing(true);

    try {
      await deleteTaskPrompt({ id: editadata._id, isScheduledTask });

      navigate("/");
      setAskToDeleteModal(false);
      closeModal();
      getUpdatePromptsData?.();

      if (typeof setIsEditPrompt === "function") {
        setIsEditPrompt(false);
      }
    } catch (error) {
      console.error("Error deleting task prompt:", error);
      toast.error("Something went wrong while deleting the task.");
    } finally {
      setDeleteProcessing(false);
    }
  };

  const handleGeneratePromptOnEnter = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleGeneratePrompt();
    }
  };

  const formContent =
    !isEdit && promptStep !== 2 ? (
      <PromptIntroStep
        pageMode={isPageMode}
        attachSlot={composerAttachSlot}
        step={promptStep}
        promptQuery={promptQuery}
        promptQueryError={promptQueryError}
        onPromptQueryChange={setPromptQuery}
        onSubmit={handleGeneratePrompt}
        onKeyDown={handleGeneratePromptOnEnter}
        onClose={closeModal}
      />
    ) : (
      <PromptDetailsForm
        isEdit={isEdit}
        promptName={promptName}
        promptNameError={promptNameError}
        onPromptNameChange={(value) => {
          setPromptName(value);
          setPromptNameError(null);
        }}
        isScheduleActive={isScheduleActive}
        onSchedulePause={handleSchedulePause}
        outputOption={outputOption}
        requiredIntegrations={requiredIntegrations}
        channelList={channelList}
        slackChannel={slackChannel}
        onOutputOptionChange={handleOutputOptionChange}
        // Store the channel_id string. Everything else here — the seed from
        // editadata.channel, loadChannels' keep/prefer logic, and the save
        // payload — works on that id; stashing a {value,label} object instead
        // made PromptDetailsForm's `channel_id === slackChannel` lookup fail,
        // so the picked channel never showed up in the control.
        onSlackChannelChange={(selectedOption) =>
          setSlackChannel(selectedOption?.value || "")
        }
        onChannelSearchInputChange={handleChannelSearchInputChange}
        onChannelMenuScrollToBottom={handleChannelMenuScrollToBottom}
        loadingChannels={loadingChannels}
        loadingMoreChannels={loadingMoreChannels}
        hasMoreChannels={hasMoreChannels}
        channelSearchQuery={channelSearchQuery}
        scheduleFrequency={scheduleFrequency}
        selectedWebhookId={selectedWebhookId}
        onSelectedWebhookIdChange={setSelectedWebhookId}
        scheduleTime={scheduleTime}
        scheduleDate={scheduleDate}
        scheduleRunCount={scheduleRunCount}
        selectedWeekDays={selectedWeekDays}
        onScheduleFrequencyChange={handleScheduleFrequencyChange}
        onScheduleTimeChange={handleScheduleTimeChange}
        onScheduleDateChange={setScheduleDate}
        onScheduleRunCountChange={setScheduleRunCount}
        onSelectedWeekDaysChange={setSelectedWeekDays}
        promptContent={promptContent}
        promptContentError={promptContentError}
        onPromptContentChange={(value) => {
          setPromptContent(value);
          setPromptContentError(null);
        }}
        promptSetAs={promptSetAs}
        onPromptSetAsChange={setPromptSetAs}
        estimatedTime={estimatedTime}
        onEstimatedTimeChange={setEstimatedTime}
        processing={processing}
        onSubmit={handlePromptSubmit}
        onDeleteClick={() => setAskToDeleteModal(true)}
        onClose={closeModal}
        agencyId={userActiveLocation?.agency_id}
        lastSavedAt={lastSavedAt}
        renderAs={renderAs}
        editadata={editadata}
        getUpdatePromptsData={getUpdatePromptsData}
      />
    );

  return (
    <div>
      {isPageMode ? (
        <div className="w-full h-full flex justify-center overflow-hidden">
          <div
            className={`w-[90%] ${pageHeader ? "max-w-3xl xl:max-w-4xl 2xl:max-w-5xl" : "max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px]"} v3chat__input !h-full overflow-y-auto mt-5 pb-10 ${pageHeader ? "" : "md:pt-10"}`}
          >
            <button
              type="button"
              onClick={closeModal}
              className="text-sm font-light !text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-10 mr-5"
            >
              ← Return to Tasks
            </button>
            {pageHeader}
            {formContent}
            {!isEdit && promptStep !== 2 && pageFooter}
          </div>
        </div>
      ) : (
        <CommonModal
          isOpen={modalIsOpen}
          onClose={closeModal}
          showAlert={false}
          title={
            <p className="text-left font-bold text-[16px]">
              {isEdit ? "Edit Task" : "Add Task"}
            </p>
          }
        >
          {formContent}
        </CommonModal>
      )}

      <DeleteTaskModal
        isOpen={askToDeleteModal}
        isScheduledTask={isScheduledTask}
        loading={deleteProcessing}
        onClose={() => setAskToDeleteModal(false)}
        onConfirm={handleDeleteTask}
      />

      {openIntegrationModal && (
        <IntegrationModal
          isOpen={openIntegrationModal}
          onClose={() => {
            setOpenIntegrationModal(false);
            setRqIntegration(null);
          }}
          rqIntegration={rqIntegration}
          requiredIntegrations={requiredIntegrations}
          integration_id={integrationId}
          onAllConnected={() => {
            if (rqIntegration) {
              fetchProviders(rqIntegration);
            }
          }}
        />
      )}
    </div>
  );
};

export default PromptModal;
